import { useState } from "react";
import { toast } from "sonner";
import { CircleNotch, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIMEZONES } from "@/components/admin/constants";
import { readAvatar } from "@/lib/avatar";

const NONE = "__none__";

const NOTIFICATION_OPTIONS = [
  ["closed_won_approvals", "Closed-Won approvals", "Alerts when an opportunity awaits (or receives) Closed-Won confirmation."],
  ["commission_updates", "Commission updates", "Alerts when commissions are approved, earned, paid or adjusted."],
  ["follow_up_reminders", "Follow-up reminders", "Reminders for follow-ups due today or overdue."],
];

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// Self-service profile: photo, preferred name, phone, time zone and
// notification preferences — no administrator assistance required.
export default function MyProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(() => ({
    preferred_name: user?.preferred_name || "",
    mobile_phone: user?.mobile_phone || "",
    timezone: user?.timezone || NONE,
    profile_picture: user?.profile_picture || null,
    notification_preferences: {
      closed_won_approvals: true,
      commission_updates: true,
      follow_up_reminders: true,
      ...(user?.notification_preferences || {}),
    },
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const togglePref = (key) => setForm((f) => ({
    ...f,
    notification_preferences: { ...f.notification_preferences, [key]: !f.notification_preferences[key] },
  }));

  const pickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readAvatar(file);
      setForm((f) => ({ ...f, profile_picture: dataUrl }));
    } catch {
      toast.error("The image could not be read.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me/profile", {
        preferred_name: form.preferred_name.trim() || null,
        mobile_phone: form.mobile_phone.trim() || null,
        timezone: form.timezone === NONE ? null : form.timezone,
        profile_picture: form.profile_picture || null,
        notification_preferences: form.notification_preferences,
      });
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="my-profile-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Account</p>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">My Profile</h1>
      <p className="mt-2 text-sm text-[#6D6D70]">
        Manage your photo, preferred name, phone, time zone and notification preferences.
        Role, quota and assignments are managed by your administrator.
      </p>

      <form onSubmit={submit} className="mt-6 max-w-xl space-y-5">
        <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
          <h2 className="text-xl font-bold text-[#032852]">Identity</h2>
          <div className="mt-4 flex items-center gap-4">
            {form.profile_picture ? (
              <img src={form.profile_picture} alt="" className="h-16 w-16 rounded-sm object-cover" data-testid="profile-avatar-preview" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-sm bg-[#032852] text-xl font-bold text-white" data-testid="profile-avatar-preview">
                {initials(user?.name)}
              </span>
            )}
            <label className="flex cursor-pointer items-center gap-1.5 rounded-sm border border-[#E4E4E7] px-3 py-1.5 text-xs font-semibold text-[#032852] hover:bg-[#F4F4F5]">
              <UploadSimple size={14} /> Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} data-testid="profile-avatar-input" />
            </label>
            {form.profile_picture && (
              <button type="button" className="text-xs font-semibold text-[#B91C1C]"
                onClick={() => setForm((f) => ({ ...f, profile_picture: null }))} data-testid="profile-avatar-remove">
                Remove
              </button>
            )}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Preferred name</Label>
              <Input value={form.preferred_name} onChange={set("preferred_name")} className="rounded-sm"
                placeholder={user?.name} data-testid="profile-preferred-name" />
              <p className="text-[11px] text-[#6D6D70]">Shown across the CRM. Your official name stays “{user?.name}”.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Mobile phone</Label>
              <Input value={form.mobile_phone} onChange={set("mobile_phone")} onBlur={(e) => set("mobile_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="profile-mobile" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Time zone</Label>
              <Select value={form.timezone} onValueChange={set("timezone")}>
                <SelectTrigger className="rounded-sm" data-testid="profile-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Use device time zone</SelectItem>
                  {TIMEZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#6D6D70]">Drives the “Local” clock in the sidebar alongside Miami HQ time.</p>
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
          <h2 className="text-xl font-bold text-[#032852]">Notification preferences</h2>
          <div className="mt-3 space-y-3">
            {NOTIFICATION_OPTIONS.map(([key, label, help]) => (
              <label key={key} className="flex cursor-pointer items-start justify-between gap-4">
                <span>
                  <span className="block text-sm font-semibold text-[#09090B]">{label}</span>
                  <span className="block text-xs text-[#6D6D70]">{help}</span>
                </span>
                <Switch checked={!!form.notification_preferences[key]} onCheckedChange={() => togglePref(key)}
                  data-testid={`profile-pref-${key}`} />
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}
            className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="profile-save-btn">
            {saving ? <CircleNotch size={18} className="animate-spin" /> : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}
